import { Division } from 'components/division';
import { Equals } from 'components/equals';
import { Multiplication } from 'components/multiplication';
import { CalculationType } from './types';
import {
  TractNRICard,
  TractWICard,
  TractNRIFormulaData,
} from 'components/tract-nri-card';
import InformationIconTrigger from 'components/information-icon-trigger';
import styles from './uat-nri-formula.module.scss';

interface UATFormulaProps {
  calculationType: CalculationType;
  hasAllocationManager?: boolean;
  tractNri?: number;
  tractNriFormula?: TractNRIFormulaData | null;
  tractNriCalculationMethod?: 'nra' | 'nma';
  instrumentType?: string;
}

const getResultLabel = (calculationType: CalculationType) => {
  return calculationType === 'NRI' ? 'Unit NRI' : 'Unit WI';
};

const TractNRISection: React.FC<{
  calculationType: CalculationType;
  tractNriFormula?: TractNRIFormulaData | null;
  tractNriCalculationMethod?: 'nra' | 'nma';
  tractNri?: number;
  instrumentType?: string;
}> = ({
  calculationType,
  tractNriFormula,
  tractNriCalculationMethod,
  tractNri,
  instrumentType,
}) => {
  const labelText = calculationType === 'NRI' ? 'Tract NRI' : 'Tract WI';

  const renderTrigger = (active: boolean, toggle: () => void) => (
    <InformationIconTrigger active={active} onClick={toggle} />
  );

  if (calculationType === 'WI' && tractNriFormula) {
    const isOGL = instrumentType?.toUpperCase() === 'OGL';
    return (
      <span className={styles.tractNriLabelContainer}>
        <span>{labelText}</span>
        {isOGL && (
          <TractWICard
            tractWiFormula={tractNriFormula}
            tractWi={tractNri ?? 0}
            renderTrigger={renderTrigger}
          />
        )}
      </span>
    );
  }

  if (tractNriFormula && tractNriCalculationMethod) {
    return (
      <span className={styles.tractNriLabelContainer}>
        <span>{labelText}</span>
        <TractNRICard
          calculationMethod={tractNriCalculationMethod}
          tractNriFormula={tractNriFormula}
          tractNri={tractNri ?? 0}
          renderTrigger={renderTrigger}
          instrumentType={instrumentType}
        />
      </span>
    );
  }

  return <span>{labelText}</span>;
};

export const UATFormula = ({
  calculationType,
  hasAllocationManager = false,
  tractNri,
  tractNriFormula,
  tractNriCalculationMethod,
  instrumentType,
}: UATFormulaProps) => {
  // When allocation manager is present, show UAT-specific labels
  // When no allocation manager, show Unit Tract labels
  const topLabel = hasAllocationManager
    ? 'UAT value (ac or ft)'
    : 'Unit Tract (Gross Acres)';
  const bottomLabel = hasAllocationManager
    ? 'Unit Tract or Total Lateral Length'
    : 'Unit Tract (Gross Acres)';
  const participationBottomLabel = hasAllocationManager
    ? 'UAT value (ac)'
    : 'Unit Tract (Gross Acres)';

  return (
    <div className={styles.container}>
      <Equals
        operandOne={
          <Multiplication
            operandOne={
              <Multiplication
                operandOne={
                  <TractNRISection
                    calculationType={calculationType}
                    tractNriFormula={tractNriFormula}
                    tractNriCalculationMethod={tractNriCalculationMethod}
                    tractNri={tractNri}
                    instrumentType={instrumentType}
                  />
                }
                operandTwo={
                  <Division
                    operandOne={<span>{topLabel}</span>}
                    operandTwo={<span>{bottomLabel}</span>}
                  />
                }
              ></Multiplication>
            }
            operandTwo={
              <Division
                operandOne={<span>Participation Tract</span>}
                operandTwo={<span>{participationBottomLabel}</span>}
              ></Division>
            }
          ></Multiplication>
        }
        operandTwo={<div>{getResultLabel(calculationType)}</div>}
      ></Equals>
    </div>
  );
};

// Keep for backwards compatibility
export const UATNRIFormula = () => <UATFormula calculationType="NRI" />;
